import * as React from 'react';
import moment from 'moment';
import { ActivityAction, ActivityResourceName } from '@pec/aion-ui-core/interfaces/activities';
import { CommonRootState } from '@pec/aion-ui-core/combineReducers';
import { connect } from 'react-redux';
import { ConnectChildren } from '@pec/aion-ui-core/types/connectChildren';
import { fetchMainSubscription } from '@pec/aion-ui-core/slices/mainSubscription';
import { fetchUserInfoIfNeeded, hasPermission } from '@pec/aion-ui-core/actions/userInfo';
import { fetchUserEulaConsent, fetchUserTosConsent } from '@pec/aion-ui-core/actions/userConsent';
import { EULA_STORAGE_KEY, TOS_STORAGE_KEY } from '@pec/aion-ui-core/constants/userConsent';
import { handleLogOut } from '@pec/aion-ui-core/actions/serverTokens';
import { Location } from 'history';
import { NavComponent } from '../../components/Nav';
import { OrganizationFeature } from '@pec/aion-ui-core/interfaces/organization';
import { RootActions } from '@pec/aion-ui-core/combineActions';
import { ThunkDispatch } from 'redux-thunk';
import { TrackerWrapper } from '@pec/aion-ui-core/components/TrackerWrapper';
import { mappedFeatureFlags } from '@pec/aion-ui-core/helpers/featureFlags';
import { TermsOfServicePage } from '../../components/TermsOfServicePage';
import { Loading } from '../../components/Loading';

const SYSTEM_ADMIN_ID = '00000000-0000-0000-0000-000000000000';

const { Manage } = ActivityAction;
const { AuditQuestions, Organizations } = ActivityResourceName;

const PROPOSE_FOUNDATION_KEY = 'organization:proposeFoundation';

type OwnProps = {
  contentEl?: HTMLElement | null;
  title?: string;
  sideNav?: JSX.Element;
  organizationId?: string;
  logoutUrl?: string;
  location: Location;
};

const mapStateToProps = (state: CommonRootState) => ({
  organization: state.organization.organization,
  userInfo: state.userInfo.userInfo,
  userOrganizations: state.userOrganizations,
  tosConsent: state.userConsent.tosConsent,
  eulaConsent: state.userConsent.eulaConsent,
  forceShowTos: state.userConsent.forceShowTos,
  featureFlags: state.featureFlags.featureFlags,
  mainSubscription: state.mainSubscription
});

const mapDispatchToProps = (dispatch: ThunkDispatch<CommonRootState, null, RootActions>, { logoutUrl }: OwnProps) => ({
  fetchUserInfoIfNeeded: () => dispatch(fetchUserInfoIfNeeded()),
  fetchUserTosConsent: (organizationId?: string) => dispatch(fetchUserTosConsent(organizationId)),
  fetchUserEulaConsent: (userId?: string) => dispatch(fetchUserEulaConsent(userId)),
  fetchMainSubscription: (organizationId: string) => dispatch(fetchMainSubscription(organizationId)),
  handleLogOut: () => dispatch(handleLogOut(logoutUrl))
});

type Props = ReturnType<typeof mapStateToProps> & ReturnType<typeof mapDispatchToProps> & OwnProps & ConnectChildren;

type State = {
  eulaProcessed: boolean;
  tosProcessed: boolean;
  isLoading: boolean;
  renderTOS: boolean;
  hideContent: boolean;
};

class Nav extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    const proposeFoundationStatus = sessionStorage.getItem(`${PROPOSE_FOUNDATION_KEY}_${props.organizationId}`);
    this.state = {
      eulaProcessed: false,
      tosProcessed: false,
      isLoading: false,
      renderTOS: false,
      hideContent: !!props.organizationId && proposeFoundationStatus !== 'false'
    };
  }

  componentDidMount() {
    const { organizationId, fetchMainSubscription } = this.props;
    organizationId && fetchMainSubscription(organizationId);
  }

  componentDidUpdate() {
    this.checkEula();
    this.checkTOS();
    this.checkSubscriptionStatus();
  }

  async checkEula() {
    const { userInfo, featureFlags } = this.props;
    const { eulaProcessed } = this.state;

    if (
      eulaProcessed ||
      featureFlags.length === 0 ||
      !userInfo ||
      (userInfo && userInfo.primaryOrganizationId === SYSTEM_ADMIN_ID)
    )
      return;

    this.setState({ eulaProcessed: true });
    const eulaFeatureIsEnabled = mappedFeatureFlags(featureFlags)['AionEula'];

    if (eulaFeatureIsEnabled && !JSON.parse(sessionStorage.getItem(EULA_STORAGE_KEY) as string)) {
      try {
        this.setState({ isLoading: true });

        const result = await this.props.fetchUserEulaConsent(userInfo.userId);
        sessionStorage.setItem(EULA_STORAGE_KEY, JSON.stringify(result));

        if (result === false) {
          window.location.assign(
            `/identity/Account/AcceptEula?userName=${userInfo?.emailAddress}&userId=${userInfo?.userId}&rememberMe=False&returnUrl=${window.location.href}`
          );
        }
      } finally {
        this.setState({ isLoading: false });
      }
    }
  }

  async checkTOS() {
    const { organizationId, userOrganizations, userInfo, featureFlags, tosConsent } = this.props;
    const { tosProcessed, renderTOS } = this.state;

    if (tosConsent && renderTOS) {
      this.setState({ renderTOS: false });
      return;
    }

    if (
      tosProcessed ||
      featureFlags.length === 0 ||
      !userInfo ||
      (userInfo && userInfo.primaryOrganizationId === SYSTEM_ADMIN_ID) ||
      userOrganizations.allUserOrganizations.length === 0
    )
      return;

    this.setState({ tosProcessed: true });
    const tosFeatureIsEnabled = mappedFeatureFlags(featureFlags)['AionTermsOfService'];

    try {
      const currentUserOrganization =
        userOrganizations.allUserOrganizations.find(userOrganization => userOrganization.id === organizationId) ||
        userOrganizations.allUserOrganizations.find(
          userOrganization => userOrganization.id === userInfo?.primaryOrganizationId
        );
      const tosKey = `${TOS_STORAGE_KEY}_${currentUserOrganization?.id}`;
      if (
        tosFeatureIsEnabled &&
        !JSON.parse(sessionStorage.getItem(tosKey) as string) &&
        !window.location.pathname.includes('SSQV4') &&
        currentUserOrganization?.features.includes(OrganizationFeature.Subscriber)
      ) {
        this.setState({ isLoading: true });

        const result = await this.props.fetchUserTosConsent(currentUserOrganization?.id);
        sessionStorage.setItem(tosKey, JSON.stringify(result));

        if (result === false) {
          this.setState({ isLoading: false, renderTOS: true });
        }
      }
    } finally {
      this.setState({ isLoading: false });
    }
  }

  async checkSubscriptionStatus() {
    const { hideContent } = this.state;
    const {
      organizationId,
      mainSubscription: { mainSubscription, isLoaded },
      userInfo
    } = this.props;
    const unlockContent = (remember: boolean) => {
      if (!hideContent) return;

      this.setState({ hideContent: false });
      if (remember) {
        sessionStorage.setItem(`${PROPOSE_FOUNDATION_KEY}_${organizationId}`, 'false');
      }
    };

    if (
      !organizationId ||
      mainSubscription.countryISO2 !== 'GB' ||
      mainSubscription.description === 'CHAS Foundation' ||
      !userInfo
    ) {
      if (isLoaded && userInfo) {
        unlockContent(true);
      }
      return;
    }
    try {
      const proposeFoundation =
        !hasPermission(userInfo, Manage, Organizations) &&
        mainSubscription.expirationDate &&
        moment().isAfter(
          moment(mainSubscription.expirationDate)
            .add(3, 'months')
            .startOf('day')
        );
      if (
        proposeFoundation &&
        ![`/organizations/${organizationId}`, `/organizations/${organizationId}/upgrade`].includes(
          window.location.pathname
        )
      ) {
        window.location.assign(`/organizations/${organizationId}`);
        return;
      }
      unlockContent(!proposeFoundation);
    } catch {}
  }

  render() {
    const {
      title,
      userInfo,
      userOrganizations,
      organization,
      children,
      sideNav,
      organizationId,
      contentEl,
      location,
      eulaConsent,
      mainSubscription: { isFetching }
    } = this.props;
    const { isLoading, renderTOS, hideContent } = this.state;
    const includeCompanySearch =
      !!userInfo && (hasPermission(userInfo, Manage, Organizations) || hasPermission(userInfo, Manage, AuditQuestions));

    return (
      <TrackerWrapper>
        {(isLoading || isFetching) && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              right: 0,
              bottom: 0,
              left: 0,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              backgroundColor: 'rgba(255,255,255, 0.5)',
              zIndex: 999
            }}
          >
            <Loading />
          </div>
        )}
        {renderTOS && eulaConsent !== false ? (
          <TermsOfServicePage organizationId={organizationId} />
        ) : (
          <NavComponent
            title={title}
            userName={userInfo && userInfo.userName}
            userEmail={userInfo && userInfo.emailAddress}
            userId={userInfo?.userId}
            handleLogOut={this.props.handleLogOut}
            sideNav={sideNav}
            includeCompanySearch={includeCompanySearch}
            organizationId={organizationId}
            contentEl={contentEl}
            location={location}
            userOrganizations={userOrganizations.allUserOrganizations}
            organization={organization}
            hasFetchedUserOrganizations={userOrganizations.hasFetched}
          >
            {!hideContent && children}
          </NavComponent>
        )}
      </TrackerWrapper>
    );
  }
}

export const NavContainer = connect(mapStateToProps, mapDispatchToProps)(Nav);
